import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import {
    doc,
    getDoc,
    collection,
    query,
    where,
    orderBy,
    limit,
    getDocs
} from "firebase/firestore";
import { db } from "../firebase";
import SearchBar from "../components/SearchBar";
import "./css/Guide.css";
import Place from "../components/Place";
import {
    updateDoc,
    increment,
    setDoc,
    deleteDoc,
    serverTimestamp,
    onSnapshot,
    writeBatch
} from "firebase/firestore";

import { useAuth } from "../context/AuthContext";

function TipsSection({ title, tips }) {
    return (
        <section className="guide-section">
            <h2>{title}</h2>
            <ul className="tips-list">
                {tips.map((tip, i) => (
                    <li key={i}>{tip}</li>
                ))}
            </ul>
        </section>
    );
}

export default function Guide() {
    const { maybeHandle } = useParams();
    const navigate = useNavigate();
    const slug = maybeHandle;
    const [guide, setGuide] = useState(null);
    const [userTrips, setUserTrips] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const isCityGuide = Boolean(guide?.city);
    const isCountryGuide = Boolean(guide && !guide.city);
    const [countryGuide, setCountryGuide] = useState(null);
    const [activePlaceSlug, setActivePlaceSlug] = useState(null);
    const [placeTypes, setPlaceTypes] = useState({});
    const { currentUser } = useAuth();
    const [liked, setLiked] = useState(false);
    const [saved, setSaved] = useState(false);

    const toSlug = (str = "") => {
        if (typeof str !== "string") return "";
        return str
            .toLowerCase()
            .trim()
            .replace(/[^a-z0-9\s-]/g, "")
            .replace(/\s+/g, "-");
    };

    useEffect(() => {
        if (!currentUser) {
            setLiked(false);
            setSaved(false);
            return;
        }

        if (!guide?.id) return;

        const checkStatus = async () => {
            const likeSnap = await getDoc(
                doc(db, "guides", guide.id, "likes", currentUser.uid)
            );

            const saveSnap = await getDoc(
                doc(db, "guides", guide.id, "saves", currentUser.uid)
            );

            setLiked(likeSnap.exists());
            setSaved(saveSnap.exists());
        };

        checkStatus();
    }, [currentUser, guide?.id]);

    useEffect(() => {
        if (!guide?.country?.name && !guide?.country?.destination) return;

        const fetchTrips = async () => {
            try {
                const tripsRef = collection(db, "trips");

                const q = query(
                    tripsRef,
                    where("visibility", "==", "public"),
                    where("destination", "==", guide.country.name || guide.country.destination),
                    orderBy("createdAt", "desc"),
                    limit(5)
                );

                const snap = await getDocs(q);

                const trips = snap.docs.map(doc => ({
                    id: doc.id,
                    ...doc.data()
                }));

                setUserTrips(trips);
            } catch (err) {
                console.error("Error fetching trips:", err);
            }
        };

        fetchTrips();
    }, [guide]);

    useEffect(() => {
        if (!isCityGuide || !guide?.country?.name) return;

        const fetchCountryGuide = async () => {
            try {
                const slug = guide.country.name.toLowerCase();
                const slugSnap = await getDoc(doc(db, "slugs", slug));
                if (!slugSnap.exists()) return;

                const countrySnap = await getDoc(
                    doc(db, "guides", slugSnap.data().guideId)
                );

                if (countrySnap.exists()) {
                    setCountryGuide(countrySnap.data());
                }
            } catch (e) {
                console.error("Failed to fetch country guide", e);
            }
        };

        fetchCountryGuide();
    }, [guide, isCityGuide]);

    useEffect(() => {
        if (!guide?.placesToVisit?.length) return;

        const fetchPlaceTypes = async () => {
            try {
                const typesMap = {};

                await Promise.all(
                    guide.placesToVisit.map(async ({ placeId, label }) => {
                        const snap = await getDoc(
                            doc(db, "places", placeId)
                        );

                        if (snap.exists()) {
                            typesMap[placeId] = snap.data().type;
                        }
                    })
                );

                setPlaceTypes(typesMap);
            } catch (err) {
                console.error("Failed to fetch place types", err);
            }
        };

        fetchPlaceTypes();
    }, [guide]);

    useEffect(() => {
        if (!guide?.id) return;

        const incrementView = async () => {
            const viewKey = `guide_viewed_${guide.id}`;

            if (!localStorage.getItem(viewKey)) {
                try {
                    await updateDoc(doc(db, "guides", guide.id), {
                        views: increment(1),
                    });

                    localStorage.setItem(viewKey, "true");
                } catch (err) {
                    console.error("Failed to increment view", err);
                }
            }
        };

        incrementView();
    }, [guide?.id]);

    useEffect(() => {
        if (!currentUser || !guide?.id) return;

        const checkStatus = async () => {
            const likeSnap = await getDoc(
                doc(db, "guides", guide.id, "likes", currentUser.uid)
            );

            const saveSnap = await getDoc(
                doc(db, "guides", guide.id, "saves", currentUser.uid)
            );

            setLiked(likeSnap.exists());
            setSaved(saveSnap.exists());
        };

        checkStatus();
    }, [currentUser, guide?.id]);


    useEffect(() => {
        if (!slug) return;

        let unsubscribe;

        const init = async () => {
            try {
                const slugRef = doc(db, "slugs", slug);
                const slugSnap = await getDoc(slugRef);

                if (!slugSnap.exists()) {
                    setError("Guide not found");
                    setLoading(false);
                    return;
                }

                const guideId = slugSnap.data().guideId;

                if (!guideId) {
                    setError("Invalid guide reference");
                    setLoading(false);
                    return;
                }

                const guideRef = doc(db, "guides", guideId);

                unsubscribe = onSnapshot(guideRef, (snap) => {
                    if (snap.exists()) {
                        setGuide({ id: snap.id, ...snap.data() });
                        setLoading(false); // ✅ IMPORTANT
                    } else {
                        setError("Guide not found");
                        setLoading(false);
                    }
                });

            } catch (err) {
                console.error(err);
                setError("Something went wrong");
                setLoading(false);
            }
        };

        init();

        return () => {
            if (unsubscribe) unsubscribe();
        };
    }, [slug]);

    const handleLike = async () => {
        if (!currentUser || !guide?.id) return;

        const guideRef = doc(db, "guides", guide.id);
        const likeRef = doc(db, "guides", guide.id, "likes", currentUser.uid);

        const likeSnap = await getDoc(likeRef);

        if (likeSnap.exists()) {
            // 🔻 UNLIKE
            if ((guide.likesCount || 0) > 0) {
                await deleteDoc(likeRef);
                await updateDoc(guideRef, {
                    likesCount: increment(-1),
                });
            }
            setLiked(false);
        } else {
            // 🔺 LIKE
            await setDoc(likeRef, {
                uid: currentUser.uid,
                createdAt: serverTimestamp(),
            });

            await updateDoc(guideRef, {
                likesCount: increment(1),
            });

            setLiked(true);
        }
    };

    const handleSave = async () => {
        if (!currentUser || !guide?.id) return;

        const uid = currentUser.uid;

        const guideRef = doc(db, "guides", guide.id);
        const saveRef = doc(db, "guides", guide.id, "saves", uid);

        const userBookmarkRef = doc(
            db,
            "users",
            uid,
            "bookmarks",
            `guide_${guide.id}`
        );

        const batch = writeBatch(db);

        if (saved) {
            // 🔴 REMOVE BOOKMARK
            batch.delete(saveRef);
            batch.delete(userBookmarkRef);
            batch.update(guideRef, {
                savesCount: increment(-1),
            });

            setSaved(false);
        } else {
            // 🟢 ADD BOOKMARK
            batch.set(saveRef, {
                uid,
                createdAt: serverTimestamp(),
            });

            batch.set(userBookmarkRef, {
                type: "guide",
                refId: guide.id,
                slug: slug,
                title: guide.guide?.title || guide.title,
                image: guide.guide?.heroImage || null,
                createdAt: serverTimestamp(),
            });

            batch.update(guideRef, {
                savesCount: increment(1),
            });

            setSaved(true);
        }

        await batch.commit();
    };


    if (loading) return <div className="guide-loading">Loading guide…</div>;
    if (error) return <div className="guide-error">{error}</div>;
    if (!guide) return null;

    const {
        guide: hero,
        quickFacts,
        country,
        sections,
        placesToVisit = [],
        travelGuide,
        flag,
        language,
        currency,
        continent,
    } = guide;

    return (
        <div className="guide-container">

            {/* HERO */}
            <section
                className="guide-hero"
                style={{
                    backgroundImage: `url(${hero.heroImage})`
                }}
            >
                <div className="guide-hero-overlay">

                    <h1>{hero.title}</h1>
                    <p>{hero.subtitle}</p>

                    {/* HERO FACTS */}
                    <div className="guide-hero-facts">
                        <div>
                            <span>🌍 Region</span>
                            <strong>{quickFacts.region}</strong>
                        </div>
                        <div>
                            <span>📅 Best Months</span>
                            <strong>{quickFacts.bestMonths}</strong>
                        </div>
                        <div>
                            <span>⏱ Duration</span>
                            <strong>{quickFacts.idealDuration}</strong>
                        </div>
                        <div>
                            <span>💰 Budget</span>
                            <strong>{quickFacts.dailyBudgetRange}</strong>
                        </div>
                        <div>
                            <span>🗺 Continent</span>
                            <strong>{country.continent}</strong>
                        </div>
                        <div>
                            <span>🗣 Language</span>
                            <strong>{country.language}</strong>
                        </div>
                        <div>
                            <span>💳 Currency</span>
                            <strong>{country.currency}</strong>
                        </div>
                        <div>
                            <span>🚩 Flag</span>
                            <strong>{country.flag}</strong>
                        </div>
                    </div>
                </div>
            </section>


            <div className="guide-layout">

                {/* MAIN */}
                <main className="guide-main">
                    <div className="guide-top-bar">
                        <button
                            className="guide-back-link"
                            onClick={() => navigate(-1)}
                        >
                            ← Go Back
                        </button>

                        <div className="guide-actions">
                            <div className="guide-stat">
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    width="16"
                                    height="16"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                >
                                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                                    <circle cx="12" cy="12" r="3" />
                                </svg>
                                <span>{guide.views || 0}</span>
                            </div>

                            <button
                                className={`guide-btn ${liked ? "active" : ""}`}
                                onClick={handleLike}
                            >
                                ❤️ {guide.likesCount || 0}
                            </button>

                            <button
                                className={`guide-btn ${saved ? "active" : ""}`}
                                onClick={handleSave}
                            >
                                🔖 {guide.savesCount || 0}
                            </button>


                        </div>
                    </div>


                    {/* WHY VISIT */}
                    <section className="guide-section">
                        <h2>{sections.whyVisit.title}</h2>
                        <p>{sections.whyVisit.description}</p>
                    </section>

                    {guide.gallery?.length > 0 && (
                        <section className="guide-section">
                            <h2>Gallery</h2>

                            <div className="gallery-grid">
                                {guide.gallery.map((img, i) => (
                                    <div
                                        key={i}
                                        className="gallery-img"
                                        style={{ backgroundImage: `url(${img})` }}
                                    />
                                ))}
                            </div>
                        </section>
                    )}


                    {/* BEST TIME */}
                    <section className="guide-section">
                        <h2>{sections.bestTimeToVisit.title}</h2>
                        <div className="info-grid">
                            {sections.bestTimeToVisit.seasons.map((s) => (
                                <div key={s.name} className="info-card">
                                    <h4>{s.emoji} {s.name}</h4>
                                    <p>{s.months} · {s.note}</p>
                                </div>
                            ))}
                        </div>
                    </section>

                    {/* REGIONS / NEIGHBORHOODS */}
                    {sections?.regionsAndRoutes && (
                        <section className="guide-section">
                            <h2>{sections.regionsAndRoutes.title}</h2>
                            <div className="region-grid">
                                {sections.regionsAndRoutes.regions.map((r) => (
                                    <div key={r.name} className="region-card">
                                        <h4>{r.emoji} {r.name}</h4>
                                        <p>{r.highlights}</p>
                                    </div>
                                ))}
                            </div>
                        </section>
                    )}

                    {/* PLACES TO VISIT — CITY ONLY */}
                    {isCityGuide && placesToVisit.length > 0 && (
                        <section className="guide-section">
                            <h2>Places of Interest</h2>

                            <ul className="places-editorial">
                                {placesToVisit.map(({ placeId, label }) => (
                                    <li
                                        key={placeId}
                                        className="place-row"
                                        onClick={() => setActivePlaceSlug(placeId)}
                                    >
                                        <div className="place-main">
                                            <h4>{label}</h4>

                                            {placeTypes[placeId] && (
                                                <span className="place-type">
                                                    {placeTypes[placeId]}
                                                </span>
                                            )}
                                        </div>


                                    </li>
                                ))}
                            </ul>

                        </section>

                    )}


                    {sections?.neighborhoods && (
                        <section className="guide-section">
                            <h2>{sections.neighborhoods.title}</h2>
                            <div className="region-grid">
                                {sections.neighborhoods.areas.map((a) => (
                                    <div key={a.name} className="region-card">
                                        <h4>{a.emoji} {a.name}</h4>
                                        <p>{a.highlights}</p>
                                    </div>
                                ))}
                            </div>
                        </section>
                    )}


                    {/* BUDGET */}
                    <section className="guide-section">
                        <h2>{sections.budget.title}</h2>
                        <div className="budget-grid">
                            {sections.budget.tiers.map((tier) => (
                                <div
                                    key={tier.type}
                                    className={`budget-card ${tier.highlight ? "highlight" : ""}`}
                                >
                                    <h4>{tier.type}</h4>
                                    <strong>{tier.range}</strong>
                                </div>
                            ))}
                        </div>
                    </section>

                    {/* GETTING AROUND */}
                    <section className="guide-section">
                        <h2>{sections.gettingAround.title}</h2>
                        <p>{sections.gettingAround.description}</p>
                    </section>

                    {/* ESSENTIAL APPS */}
                    <section className="guide-section">
                        <h2>{travelGuide.essentialApps.title}</h2>
                        <div className="info-grid">
                            {travelGuide.essentialApps.categories.map((cat) => (
                                <div key={cat.category} className="info-card">
                                    <h4>{cat.category}</h4>
                                    <p>{cat.apps.join(" · ")}</p>
                                </div>
                            ))}
                        </div>
                    </section>

                    <TipsSection
                        title={travelGuide.moneyAndPayments.title}
                        tips={travelGuide.moneyAndPayments.tips}
                    />

                    <TipsSection
                        title={travelGuide.safetyAndTips.title}
                        tips={travelGuide.safetyAndTips.tips}
                    />

                    <TipsSection
                        title={travelGuide.simAndEsim.title}
                        tips={travelGuide.simAndEsim.options}
                    />

                    <TipsSection
                        title={travelGuide.transportAndCabs.title}
                        tips={travelGuide.transportAndCabs.tips}
                    />

                    <TipsSection
                        title={travelGuide.wifiAndInternet.title}
                        tips={travelGuide.wifiAndInternet.tips}
                    />

                    {/* CTA */}
                    {/* <section className="guide-cta">
                        <h3>{sections.cta.title}</h3>
                        <p>{sections.cta.subtitle}</p>
                        <div className="cta-actions">
                            <button className="primary-btn">+ Add Trip</button>
                            <button className="ghost-btn">Save Guide</button>
                        </div>
                    </section> */}

                </main>

                {/* SIDEBAR */}
                <aside className="guide-sidebar">

                    <div>
                        <h4>Search trips</h4>
                        <SearchBar />
                    </div>

                    {/* SIDEBAR PLACES — COUNTRY GUIDE */}
                    {isCountryGuide && placesToVisit.length > 0 && (
                        <div className="sidebar-card">
                            <h4>Places to visit</h4>
                            <ul className="places-list">
                                {placesToVisit.map((place) => (
                                    <li key={place.placeId}>
                                        <Link to={`/${place.placeId}`}>
                                            {place.label}
                                        </Link>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}


                    {/* SIDEBAR — CITY GUIDE (COUNTRY PLACES + LINK) */}
                    {isCityGuide && countryGuide?.placesToVisit?.length > 0 && (
                        <div className="sidebar-card">
                            <h4>Places to visit in {countryGuide.title}</h4>

                            <ul className="places-list">
                                {countryGuide.placesToVisit.map((place) => (
                                    <li key={place.placeId}>
                                        <Link to={`/${place.placeId}`}>
                                            {place.label}
                                        </Link>
                                    </li>
                                ))}
                            </ul>

                            <Link
                                to={`/${countryGuide.slug || countryGuide.id}`}
                                className="sidebar-country-link"
                            >
                                {countryGuide.title} →
                            </Link>
                        </div>
                    )}
                    <div className="sidebar-card">
                        <h4>User trips</h4>

                        {userTrips.length === 0 && (
                            <p className="muted">No public trips yet</p>
                        )}

                        {userTrips.map((trip) => (
                            <Link
                                key={trip.id}
                                to={`/trips/${trip.slug}`}
                                className="sidebar-trip"
                            >
                                <div
                                    className="sidebar-trip-img"
                                    style={{
                                        backgroundImage: `url(${trip.media?.[0] || "/trip-placeholder.jpg"})`
                                    }}
                                />
                                <div>
                                    <div className="sidebar-trip-title">
                                        {trip.title}
                                    </div>
                                    <div className="sidebar-trip-meta">
                                        {trip.startLocation} → {trip.destination}
                                    </div>
                                </div>
                            </Link>
                        ))}
                    </div>
                    {activePlaceSlug && (
                        <Place
                            placeId={activePlaceSlug}
                            onClose={() => setActivePlaceSlug(null)}
                        />
                    )}

                </aside>
            </div>
        </div>
    );
}
